import {
  IconCheck, IconChevronDown, IconPencil, IconShare, IconTrash, IconUsers, IconUserPlus,
} from '@tabler/icons-react'
import {
  AssignProjectModal,
  Box,
  Button,
  EditStudentsModal,
  Group,
  Menu,
  RemoveConfirmModal,
} from 'components'
import { useCurrentUser } from 'context'
import { hasPermission } from 'hooks'
import type { FC } from 'react'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ArcProject } from '../../types'
import { ShareWithTeachersModal } from '../ShareWithTeachersModal'
import { useProjectCardHandlers } from './useProjectCardHandlers'

interface ArcProjectCardActionsProps {
  project: ArcProject
  canEdit: boolean
  isLibrary?: boolean
  backlink?: string
  hideViewButton?: boolean
}

/**
 * Action buttons for project cards (view, edit, assign, delete)
 * Handles different actions based on project status and context (library vs my projects)
 */
export const ArcProjectCardActions: FC<ArcProjectCardActionsProps> = memo(({
  project,
  canEdit,
  isLibrary = false,
  backlink,
  hideViewButton = false,
}) => {
  const { t } = useTranslation()
  const { currentUser } = useCurrentUser()

  const isDraft = project.status === 'draft'
  const isAssigned = project.status === 'approved'
  const isCompleted = project.status === 'finished'
  const isTemplate = project.kind === 'template'
  const isApplication = project.kind === 'application'
  const isAuthor = currentUser?.id === project.authorId
  const isTutorOrManager = hasPermission(['tutor', 'manager'])
  const isManager = hasPermission(['manager'])

  const showActionsMenu = canEdit && (isDraft || isAssigned || isCompleted)
  const showEditButton = canEdit && isAssigned && !showActionsMenu
  const canAssign = isLibrary ? false : (isDraft && isTemplate && (isAuthor || isManager))
  const canComplete = isTutorOrManager && isApplication && isAssigned

  const {
    assignModalOpened, closeAssignModal, openAssignModal,
    editStudentsOpened, setEditStudentsOpened, closeEditStudents,
    deleteModalOpened, openDeleteModal, closeDeleteModal,
    shareModalOpened, setShareModalOpened,
    isAssigning,
    handleView, handleEdit,
    handleAssignSave, handleDeleteConfirm, handleComplete,
  } = useProjectCardHandlers({ project, isLibrary, backlink })

  return (
    <Box>
      <Group
        position={hideViewButton ? 'right' : isLibrary ? 'center' : 'apart'}
        mt={hideViewButton ? 0 : 'md'}
        noWrap
      >
        {!hideViewButton && (
          <Button variant="subtle" size="sm" color="teal" onClick={handleView} styles={{ root: { height: 44 } }}>
            {t('arc_projects.view_project')}
          </Button>
        )}

        {!isLibrary && showActionsMenu && (
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <Button
                variant="filled"
                size="sm"
                color="teal"
                leftIcon={<IconPencil size={16} />}
                rightIcon={<IconChevronDown size={14} />}
                styles={{ root: { height: 44, paddingLeft: 12, paddingRight: 10 } }}
              >
                {t('arc_projects.actions')}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {!isCompleted && (
                <Menu.Item icon={<IconPencil size={16} />} onClick={handleEdit}>
                  {t('arc_projects.edit')}
                </Menu.Item>
              )}
              <Menu.Item icon={<IconShare size={16} />} onClick={() => setShareModalOpened(true)}>
                {t('arc_projects.share_with_teachers')}
              </Menu.Item>
              {isAssigned && (
                <Menu.Item icon={<IconUsers size={16} />} onClick={() => setEditStudentsOpened(true)}>
                  {t('arc_projects.edit_assigned_students')}
                </Menu.Item>
              )}
              {canAssign && (
                <Menu.Item icon={<IconUserPlus size={16} />} onClick={openAssignModal}>
                  {t('arc_projects.assign_project')}
                </Menu.Item>
              )}
              {canComplete && (
                <Menu.Item icon={<IconCheck size={16} />} onClick={handleComplete}>
                  {t('arc_projects.complete_project')}
                </Menu.Item>
              )}
              {isDraft && (
                <>
                  <Menu.Divider />
                  <Menu.Item icon={<IconTrash size={16} />} color="red" onClick={openDeleteModal}>
                    {t('arc_projects.delete_project')}
                  </Menu.Item>
                </>
              )}
            </Menu.Dropdown>
          </Menu>
        )}

        {!isLibrary && showEditButton && (
          <Button
            variant="filled"
            size="sm"
            color="teal"
            leftIcon={<IconPencil size={16} />}
            onClick={handleEdit}
            styles={{ root: { height: 44 } }}
          >
            {t('arc_projects.edit_project')}
          </Button>
        )}
      </Group>

      <AssignProjectModal
        opened={assignModalOpened}
        onClose={closeAssignModal}
        onSave={handleAssignSave}
        projectId={project.id}
        projectName={project.name}
        isLoading={isAssigning}
      />

      <EditStudentsModal
        opened={editStudentsOpened}
        onClose={closeEditStudents}
        projectId={project.id}
        schoolTermId={project.schoolTermId || ''}
        groupName={project.groupName || ''}
      />

      <RemoveConfirmModal
        type="project"
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        actionCancelConfirm={handleDeleteConfirm}
      />

      <ShareWithTeachersModal
        opened={shareModalOpened}
        onClose={() => setShareModalOpened(false)}
        projectId={project.id}
        copyOnly={isCompleted}
      />
    </Box>
  )
})

ArcProjectCardActions.displayName = 'ArcProjectCardActions'
